import React, { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import { MoreHorizontal, Pencil, Receipt, Ban, Trash2, PowerOff, Power, Package } from "lucide-react";
import EditFirmDialog from "./EditFirmDialog";
import BillingDialog from "./BillingDialog";
import AssignSubscriptionDialog from "./AssignSubscriptionDialog";
import { tierMeta, fmtDate } from "./utils";
import { useConfirm } from "../../components/ConfirmProvider";

export default function FirmsTab({ workspaces, onReload }) {
  const [editWs, setEditWs] = useState(null);
  const [billWs, setBillWs] = useState(null);
  const [subWs, setSubWs] = useState(null);
  const confirm = useConfirm();

  const toggleSuspend = async (ws) => {
    const suspending = ws.status === "active";
    const ok = await confirm({
      title: suspending ? `Suspend ${ws.name}?` : `Reactivate ${ws.name}?`,
      message: suspending
        ? "Suspended firms can't log in until you reactivate them. Their data is kept intact."
        : "This firm will be able to sign in and use the platform again.",
      confirmText: suspending ? "Suspend" : "Activate",
      danger: suspending,
    });
    if (!ok) return;
    try {
      await api.post(`/superadmin/workspaces/${ws.workspace_id}/toggle-suspend`);
      toast.success(`${ws.name} ${suspending ? "suspended" : "reactivated"}`);
      onReload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (ws) => {
    const ok = await confirm({
      title: `Permanently delete ${ws.name}?`,
      message: `This wipes the firm, every project, every VO, every user in it. This cannot be undone.`,
      confirmText: "Delete forever",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/superadmin/workspaces/${ws.workspace_id}`);
      toast.success("ID firm deleted");
      onReload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-3">
      {workspaces.length === 0 && (
        <div className="card-soft text-center text-[#7A7571] py-12">No ID firms yet. Click "Add ID firm" above.</div>
      )}
      {workspaces.map((ws) => {
        const suspended = ws.status === "suspended";
        const tier = tierMeta(ws.plan_tier);
        const rs = ws.renewal_status || {};
        // Pastel pill matching the renewal category so the platform team
        // can eyeball who to chase next.
        const renewalPill = rs.category === "expired"
          ? { label: "Expired", bg: "#FEE2E2", fg: "#9F1239" }
          : rs.category === "renewal_soon"
          ? { label: `Renewal in ${rs.days_left}d`, bg: "#FEF3C7", fg: "#92400E" }
          : rs.category === "active"
          ? { label: `${rs.days_left}d left`, bg: "#D1FAE5", fg: "#065F46" }
          : null;
        return (
          <div key={ws.workspace_id}
            data-testid={`firm-row-${ws.workspace_id}`}
            className={`card-soft flex flex-col lg:flex-row lg:items-center gap-6 !p-6 ${suspended ? "opacity-60" : ""}`}>
            {/* Left: name + tier */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-display text-xl font-bold text-[#2C2A29] leading-tight">{ws.name}</h3>
                <span className="badge" style={{ background: tier.bg, color: tier.fg }}>{tier.label}</span>
                {renewalPill && (
                  <span className="badge"
                    style={{ background: renewalPill.bg, color: renewalPill.fg }}
                    data-testid={`firm-renewal-${ws.workspace_id}`}>
                    {renewalPill.label}
                  </span>
                )}
                {suspended && <span className="badge badge-rejected">Suspended</span>}
              </div>
              <div className="text-xs text-[#7A7571] mt-1">ID: {ws.workspace_id}</div>
            </div>

            {/* Subscription dates */}
            <div className="lg:w-56">
              <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Subscription</div>
              <div className="text-sm text-[#2C2A29] mt-1">
                {fmtDate(ws.subscription_start_date)} → {fmtDate(ws.subscription_end_date)}
              </div>
            </div>

            {/* Counts */}
            <div className="flex items-center gap-6 lg:w-40">
              <div>
                <div className="text-xs text-[#7A7571]">Users</div>
                <div className="font-display text-xl font-bold text-[#2C2A29]" data-testid={`firm-users-${ws.workspace_id}`}>{ws.user_count}</div>
              </div>
              <div>
                <div className="text-xs text-[#7A7571]">Projects</div>
                <div className="font-display text-xl font-bold text-[#2C2A29]" data-testid={`firm-projects-${ws.workspace_id}`}>{ws.project_count}</div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => toggleSuspend(ws)}
                data-testid={`firm-suspend-${ws.workspace_id}`}
                className={`inline-flex items-center gap-1 !py-2 !px-3 text-sm ${suspended ? "btn-primary" : "btn-secondary"}`}
              >
                {suspended ? <><Power size={14} /> Activate</> : <><PowerOff size={14} /> Suspend</>}
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid={`firm-more-${ws.workspace_id}`} className="btn-ghost !p-2" title="More">
                    <MoreHorizontal size={18} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-2xl border-[#EBE5D9]">
                  <DropdownMenuItem data-testid={`firm-edit-${ws.workspace_id}`} onClick={() => setEditWs(ws)} className="cursor-pointer rounded-xl">
                    <Pencil size={14} className="mr-2" /> Edit firm
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid={`firm-manage-sub-${ws.workspace_id}`} onClick={() => setSubWs(ws)} className="cursor-pointer rounded-xl">
                    <Package size={14} className="mr-2" /> Manage subscription
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid={`firm-billing-${ws.workspace_id}`} onClick={() => setBillWs(ws)} className="cursor-pointer rounded-xl">
                    <Receipt size={14} className="mr-2" /> Billing / Invoices
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid={`firm-delete-${ws.workspace_id}`} onClick={() => del(ws)} className="cursor-pointer rounded-xl text-[#9F1239] focus:text-[#9F1239]">
                    <Trash2 size={14} className="mr-2" /> Delete firm
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        );
      })}

      <EditFirmDialog open={!!editWs} onOpenChange={(v) => !v && setEditWs(null)} workspace={editWs} onDone={onReload} />
      <BillingDialog open={!!billWs} onOpenChange={(v) => !v && setBillWs(null)} workspace={billWs} />
      {subWs && (
        <AssignSubscriptionDialog
          workspace={subWs}
          onClose={() => setSubWs(null)}
          onSaved={onReload}
        />
      )}
    </div>
  );
}
